import React, { useContext, useEffect, useState } from "react";
import { ProcessContext } from "../../../../../contexts/ProcessContext";

import GDPROptinsDialog from "../shared/components/GDPROptinsDialog/GDPROptinsDialog";
import useGDPRHandler from "../shared/hooks/useGDPRHandler";
import { useLoanApplication } from "../shared/hooks/useLoanApplication";
import { useLoanCalculator } from "../shared/hooks/useLoanCalculator/useLoanCalculator";
import { ConfigContext } from "../../../../../contexts/ConfigContext";
import {
  CustomRegistry,
  EmploymentStatus,
  OnboardingDetailsRole,
} from "@nemuru-eng/js-api-client";
import { CardSummaryWrapper } from "./components/CardSummaryWrapper";
import { RiskAnalysisOptIns } from "../LoanSummary/components/RiskAnalysisOptIns";
import { CARD_SUMMARY_STEP_NAMES } from "../config";
import { CardConditions } from "./components/CardConditions";
import { CardInsurance } from "../../../card/Card/components/CardInsurance";
import {
  INSURANCE_PACK,
  insuranceMapper,
  PROTECTION_PLUS_PACK_MIN_AGE,
  SENIOR_PACK_MAX_AGE,
  SENIOR_PACK_MIN_AGE,
} from "../../../card/Card/config";
import { useActionCallback } from "../../../../../utils/hooks/useActionCallback";
import { calculateAgeFromBirthDate } from "../../../../../utils/dateHelpers";
import { InsuranceProvider } from "@nemuru-eng/js-api-client/slim";

export function CardSummary() {
  const {
    state: { participant, loan, asset },
    dispatch,
  } = useContext(ProcessContext);

  const {
    state: { registry },
  } = useContext(ConfigContext);

  const { optInsArray, areOptInsSynced, submitOptInsConsents } =
    useGDPRHandler();

  const { patchSetLoanCalculator } = useLoanCalculator();

  const {
    handleRequestLoanOrShowOptinsDialog,
    acceptTerms,
    getValues,
    form,
    showOptinsDialog,
    setShowOptinsDialog,
  } = useLoanApplication({
    submitOptInsConsents,
    optInsArray,
    areOptInsSynced,
    patchSetLoanCalculator,
  });

  const [state, setState] = useState({
    currentModuleStepToShow:
      registry === CustomRegistry.CAIXABANK
        ? CARD_SUMMARY_STEP_NAMES.RISK_ANALYSIS_OPT_INS
        : CARD_SUMMARY_STEP_NAMES.CARD_SUMMARY,
  });

  const { currentModuleStepToShow } = state;

  useEffect(() => {
    if (loan && !asset) {
      dispatch({
        type: "REQUEST_GET_ASSET_DETAILS",
        payload: {
          assetId: loan.asset_reference[0].id,
          assetType: loan.asset_reference[0].type,
        },
      });
    }
  }, [loan, asset]);

  const setModuleStepGroup = (moduleStepGroup) => {
    setState((s) => ({
      ...s,
      currentModuleStepToShow: moduleStepGroup,
    }));
  };

  const getInsuranceByBorrowerOnBoardingDetails = () => {
    const borrowerOnBoardingDetails = loan.onboarding_details.find(
      (details) => details.role === OnboardingDetailsRole.BORROWER
    );

    if (!borrowerOnBoardingDetails) return null;

    const borrowerAge = calculateAgeFromBirthDate(
      borrowerOnBoardingDetails.user_details.document_id.birth_date
    );

    if (
      borrowerAge > SENIOR_PACK_MAX_AGE ||
      borrowerAge < PROTECTION_PLUS_PACK_MIN_AGE ||
      (borrowerOnBoardingDetails.user_details.professional_details
        .employment_status === EmploymentStatus.PENSIONER &&
        borrowerAge < SENIOR_PACK_MIN_AGE &&
        borrowerAge > PROTECTION_PLUS_PACK_MIN_AGE)
    ) {
      return null;
    }

    if (borrowerAge >= SENIOR_PACK_MIN_AGE) {
      return INSURANCE_PACK.SENIOR;
    }

    return INSURANCE_PACK.PROTECTION_PLUS;
  };

  const insurancePack = getInsuranceByBorrowerOnBoardingDetails();

  useActionCallback(
    "REQUEST_PATCH_UPDATE_LOAN_CONDITIONS_BY_INSURANCE_FULFILLED",
    () => {
      handleRequestLoanOrShowOptinsDialog();
    }
  );

  useActionCallback(
    "REQUEST_PATCH_UPDATE_LOAN_CONDITIONS_BY_INSURANCE_REJECTED",
    () => {
      dispatch({
        type: "SET_FLOW_LOADING",
        payload: {
          show: false,
        },
      });
    }
  );

  const onSubmitCard = async (values) => {
    dispatch({
      type: "SET_FLOW_LOADING",
      payload: {
        show: true,
      },
    });

    if (values.cardInsurance) {
      const insurance = insuranceMapper(insurancePack);
      const dataToSend = {
        id: loan.id,
        insurance_type: insurance,
        insurance_provider: InsuranceProvider.CAIXABANK,
      };
      await dispatch({
        type: "REQUEST_PATCH_UPDATE_LOAN_CONDITIONS_BY_INSURANCE",
        payload: dataToSend,
      });
      return;
    }

    handleRequestLoanOrShowOptinsDialog();
  };

  return (
    <>
      {showOptinsDialog ? (
        <GDPROptinsDialog
          participant={participant}
          form={form}
          showOptinsDialog={showOptinsDialog}
          acceptTerms={acceptTerms}
          optInsArray={optInsArray}
          areOptInsSynced={areOptInsSynced}
          setShowOptinsDialog={setShowOptinsDialog}
        />
      ) : (
        <>
          {currentModuleStepToShow ===
            CARD_SUMMARY_STEP_NAMES.RISK_ANALYSIS_OPT_INS && (
            <RiskAnalysisOptIns
              setModuleNextStep={() =>
                setModuleStepGroup(CARD_SUMMARY_STEP_NAMES.CARD_SUMMARY)
              }
            />
          )}
          {currentModuleStepToShow === CARD_SUMMARY_STEP_NAMES.CARD_SUMMARY &&
            asset &&
            (!insurancePack ? (
              <CardSummaryWrapper
                handleRequestLoanOrShowOptinsDialog={
                  handleRequestLoanOrShowOptinsDialog
                }
                getValues={getValues}
                ConditionsComponent={
                  <CardConditions cardAssetDetails={asset} />
                }
              />
            ) : (
              <CardInsurance
                onSubmitForm={onSubmitCard}
                insurancePack={insurancePack}
              />
            ))}
        </>
      )}
    </>
  );
}
